import { Icons } from '@/components/icons';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { cn } from '@/lib/cn';
import { useGetCount } from '@/requests/hooks';
import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch, type Path } from 'react-hook-form';
import type { SnakeCase } from 'type-fest';
import { useDebounceCallback } from 'usehooks-ts';
import type { FieldProps } from '../types';
import { DescriptionTooltip } from './description-tooltip';

export function UniqueText<Instance extends BaseInstance = BaseInstance>({
  formField,
  name,
  placeholder,
  label,
  description,
  horizontal,
  className,
  disabled,
  canHandleField,
  modelName,
  validateAgainst,
  onValueChange,
  validate,
}: FieldProps<Instance> & {
  modelName: ModelName;
  validateAgainst: SnakeCase<keyof Instance>;
  onValueChange?: (value: unknown) => void;
}) {
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounceCallback(setSearch, 500);
  const { setError, clearErrors, formState } = useFormContext();
  const { dirtyFields, errors } = formState;
  const isFieldDirty = !!dirtyFields?.[String(formField.name)];
  const { data, isLoading } = useGetCount({
    modelName: modelName,
    filters: {
      [validateAgainst]: {
        __iexact: search.trim(),
      },
    },
    enabled: !!search && isFieldDirty,
  });
  const value = useWatch({
    control: formField.control,
    name: name as Path<Instance>,
  });

  useEffect(() => {
    if (data && data > 0 && isFieldDirty && search === value) {
      setError(
        String(formField.name),
        {
          message: 'Detta värde är redan taget',
          type: 'unique',
        },
        {
          shouldFocus: true,
        }
      );
    } else {
      clearErrors(String(formField.name));
    }
  }, [
    clearErrors,
    data,
    formField.name,
    isFieldDirty,
    search,
    setError,
    value,
  ]);

  useEffect(() => {
    if (value) {
      debouncedSearch(value);
    }
  }, [name, value, debouncedSearch]);
  const isListEmpty =
    (!data || data === 0) && search === value && search !== '';
  const isInvalid = !isLoading && errors?.[String(formField.name)] != null;
  return (
    <FormField
      control={formField.control}
      rules={{
        required: formField.required && 'Detta fält är obligatoriskt',
        validate,
      }}
      name={name as Path<Instance>}
      render={({ field }) => (
        <FormItem
          className={cn(
            'flex items-center justify-start',
            !horizontal && 'flex w-full flex-col items-start',
            className
          )}
        >
          <div className={cn(horizontal ? 'w-1/2 shrink-0' : 'w-full')}>
            <div className={cn('flex items-start')}>
              <FormLabel
                className={cn(formField.required && "after:content-['*']")}
              >
                {label ?? formField.label}{' '}
              </FormLabel>
              {(description || formField.description) && (
                <DescriptionTooltip
                  description={description ?? formField.description}
                />
              )}
            </div>
            {horizontal && <FormMessage />}
          </div>
          <FormControl>
            <InputGroup aria-invalid={isInvalid}>
              <InputGroupInput
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  onValueChange?.(e);
                }}
                value={field.value ?? ''}
                placeholder={placeholder ?? formField.label}
                disabled={disabled ?? field.disabled ?? !canHandleField}
                aria-invalid={isInvalid}
                className='h-10'
                autoComplete='off'
              />
              <InputGroupAddon align='inline-end'>
                {isLoading && <Icons.loader className='size-4 animate-spin' />}
                {!isLoading && isListEmpty && !isInvalid && (
                  <Icons.check className='size-4 text-success' />
                )}
                {isInvalid && (
                  <Icons.alert className='size-4 text-destructive' />
                )}
              </InputGroupAddon>
            </InputGroup>
          </FormControl>
          {!horizontal && <FormMessage />}
        </FormItem>
      )}
    />
  );
}
